import { COMPANY } from "@/lib/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";

const Editable = ({ children }) => (
    <div className="my-5 rounded-xl border border-dashed border-amber-400 bg-amber-50 p-4 text-xs leading-relaxed text-amber-800" data-testid="editable-note">
        <strong>TO CONFIRM BEFORE PUBLISHING — </strong>
        {children}
    </div>
);

const LegalLayout = ({ title, description, updated, children, testId }) => (
    <>
        <SEO title={`${title} | Virtex Renewables`} description={description} />
        <section className="bg-slate-50 py-14 md:py-20">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">{title}</h1>
                    <p className="mt-3 text-sm text-slate-500">Last updated: {updated}</p>
                </Reveal>
            </div>
        </section>
        <section className="bg-white py-14 md:py-20" data-testid={testId}>
            <div className="mx-auto max-w-3xl space-y-8 px-4 text-base leading-relaxed text-slate-600 sm:px-6 lg:px-8 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-6">
                {children}
            </div>
        </section>
    </>
);

export const PrivacyPolicy = () => (
    <LegalLayout
        title="Privacy Policy"
        description="How Virtex Renewables Ltd collects, uses and protects your personal data."
        updated="January 2026"
        testId="privacy-policy-page"
    >
        <div>
            <h2>Who We Are</h2>
            <p>
                Virtex Renewables Ltd (Company No. {COMPANY.companyNumber}) is the data controller for personal data
                collected through this website. Our registered office is Flat 2 Park Lane Court, 31 Parsonage Road,
                Manchester, England, M20 4PS. You can contact us about anything in this policy at{" "}
                <a href={COMPANY.emailHref} className="font-semibold text-emerald-700 hover:underline">{COMPANY.email}</a>.
            </p>
        </div>
        <div>
            <h2>What We Collect</h2>
            <p>When you submit an enquiry through this website, we collect:</p>
            <ul>
                <li>Your name, telephone number and email address</li>
                <li>Your postcode and property details (property type, bedrooms, current heating system, property status)</li>
                <li>Any additional information you choose to include in your message</li>
            </ul>
        </div>
        <div>
            <h2>How We Use Your Data</h2>
            <p>We use the information you provide to respond to your enquiry, discuss your heating requirements and, where requested, arrange a property assessment. Our lawful bases are your consent and taking steps at your request prior to entering into a contract.</p>
        </div>
        <div>
            <h2>Sharing Your Data</h2>
            <p>
                We do not sell your personal data. Your information may be processed by service providers who support
                our website and enquiry handling, under appropriate contractual safeguards.
            </p>
        </div>
        <div>
            <h2>How Long We Keep Your Data</h2>
            <Editable>
                Confirm the retention period for enquiry data (for example, "enquiries that do not proceed are deleted
                after 12 months") with the company before this policy is relied upon.
            </Editable>
        </div>
        <div>
            <h2>Your Rights</h2>
            <p>
                Under UK data protection law you have the right to access, correct, erase, restrict or object to the
                processing of your personal data, and the right to data portability in certain circumstances. To
                exercise any of these rights, contact {COMPANY.email}.
            </p>
            <p>
                You also have the right to complain to the Information Commissioner's Office (ICO) at ico.org.uk if
                you are unhappy with how we handle your data.
            </p>
        </div>
        <div>
            <h2>Changes to This Policy</h2>
            <p>We may update this policy from time to time. The latest version will always be published on this page.</p>
        </div>
    </LegalLayout>
);

export const CookiePolicy = () => (
    <LegalLayout
        title="Cookie Policy"
        description="How the Virtex Renewables website uses cookies."
        updated="January 2026"
        testId="cookie-policy-page"
    >
        <div>
            <h2>What Are Cookies?</h2>
            <p>
                Cookies are small text files stored on your device when you visit a website. Similar technologies,
                such as browser local storage, can also store information on your device.
            </p>
        </div>
        <div>
            <h2>How This Website Uses Cookies</h2>
            <p>
                This website currently uses only essential storage required for the site to function — for example,
                remembering that you have dismissed the cookie notice. We do not currently set marketing, advertising
                or analytics tracking cookies.
            </p>
            <ul>
                <li><strong>Cookie notice preference</strong> — stored in your browser so the notice is not shown on every visit. Retained until you clear your browser storage.</li>
            </ul>
        </div>
        <div>
            <h2>Managing Cookies</h2>
            <p>
                You can control and delete cookies through your browser settings. Blocking essential storage may
                affect how parts of this website function.
            </p>
        </div>
        <Editable>
            If analytics or marketing cookies are introduced in future, update this policy and ensure consent is
            obtained before any non-essential cookies are set.
        </Editable>
        <div>
            <h2>Contact</h2>
            <p>
                Questions about this policy can be sent to{" "}
                <a href={COMPANY.emailHref} className="font-semibold text-emerald-700 hover:underline">{COMPANY.email}</a>.
            </p>
        </div>
    </LegalLayout>
);

export const TermsAndConditions = () => (
    <LegalLayout
        title="Terms & Conditions"
        description="Terms of use for the Virtex Renewables website."
        updated="January 2026"
        testId="terms-page"
    >
        <div>
            <h2>About These Terms</h2>
            <p>
                These terms govern your use of the Virtex Renewables website ({COMPANY.website}), operated by Virtex
                Renewables Ltd (Company No. {COMPANY.companyNumber}), registered office Flat 2 Park Lane Court,
                31 Parsonage Road, Manchester, England, M20 4PS.
            </p>
        </div>
        <div>
            <h2>Information on This Website</h2>
            <p>
                The content of this website is provided for general information only. It does not constitute technical
                advice, a quotation or an offer capable of acceptance. Any proposal for installation work follows a
                property assessment and is subject to a separate written quotation and contract.
            </p>
        </div>
        <div>
            <h2>Grant Schemes</h2>
            <p>
                References to the Boiler Upgrade Scheme or any other grant scheme describe publicly available scheme
                information. Eligibility, funding levels and scheme rules are set by the scheme operator and
                government and may change. Nothing on this website guarantees that any grant will be awarded.
            </p>
        </div>
        <div>
            <h2>Intellectual Property</h2>
            <p>
                The content, branding and design of this website are owned by or licensed to Virtex Renewables Ltd
                and may not be reproduced without permission.
            </p>
        </div>
        <div>
            <h2>Liability</h2>
            <p>
                We take reasonable care to keep the information on this website accurate, but we do not warrant that
                it is complete or error-free. Nothing in these terms excludes liability that cannot be excluded by law.
            </p>
        </div>
        <div>
            <h2>Governing Law</h2>
            <p>These terms are governed by the laws of England and Wales.</p>
        </div>
        <Editable>
            Confirm any additional commercial terms (quotation validity, deposit and payment terms, cancellation
            rights under the Consumer Contracts Regulations) before contracts are issued through the business.
        </Editable>
    </LegalLayout>
);

export const Complaints = () => (
    <LegalLayout
        title="Complaints"
        description="How to raise a complaint with Virtex Renewables and how we handle it."
        updated="January 2026"
        testId="complaints-page"
    >
        <div>
            <h2>How to Raise a Complaint</h2>
            <p>
                If you are unhappy with any aspect of our service, we want to hear about it so we can put things
                right. You can contact us by:
            </p>
            <ul>
                <li>Telephone: {COMPANY.phoneDisplay}</li>
                <li>Email: {COMPANY.email}</li>
                <li>Post: Virtex Renewables Ltd, Flat 2 Park Lane Court, 31 Parsonage Road, Manchester, England, M20 4PS</li>
            </ul>
            <p>Please include your name, contact details, postcode and a description of your concern.</p>
        </div>
        <div>
            <h2>What Happens Next</h2>
            <ul>
                <li>We will acknowledge your complaint and let you know who is handling it.</li>
                <li>We will investigate the matter and may contact you for further information.</li>
                <li>We will provide a written response setting out our findings and any proposed resolution.</li>
            </ul>
            <Editable>
                Confirm acknowledgement and final-response timescales (for example, acknowledge within 5 working days,
                respond within 8 weeks) in line with any consumer code or certification body requirements once
                membership is confirmed.
            </Editable>
        </div>
        <div>
            <h2>If You Remain Unhappy</h2>
            <Editable>
                Add escalation route details (for example, an Alternative Dispute Resolution body or consumer code
                scheme) once Virtex Renewables' memberships are confirmed. Do not publish a scheme name until
                membership is in place.
            </Editable>
        </div>
    </LegalLayout>
);
